// The one rejection every request through api/transport ends in. `status` is
// null when nothing came back from the server (network, timeout, aborted, or
// `unknown`: something threw before the request was sent, or the answer was
// an HTML document instead of the API's JSON — a bug or a misroute either way);
// `code` is the backend's `error` key when the body carries one
// ({ error: "not_subscribed" }), so callers classify with `is(status, code)`
// instead of reading into `data` themselves.

export type ApiErrorKind =
  | "http"
  | "network"
  | "timeout"
  | "aborted"
  | "unknown";

type ApiErrorInit = {
  kind?: ApiErrorKind;
  status?: number | null;
  data?: unknown;
  message?: string;
};

const readCode = (data: unknown): string | null => {
  if (typeof data !== "object" || data === null) return null;
  const code = (data as { error?: unknown }).error;
  return typeof code === "string" ? code : null;
};

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number | null;
  readonly data: unknown;
  readonly code: string | null;

  constructor({
    kind,
    status = null,
    data = null,
    message,
  }: ApiErrorInit = {}) {
    super(message ?? (status === null ? "Network error" : `HTTP ${status}`));
    this.name = "ApiError";
    this.kind = kind ?? (status === null ? "network" : "http");
    this.status = status;
    this.data = data;
    this.code = readCode(data);
  }

  is(status: number, code?: string): boolean {
    return this.status === status && (code === undefined || this.code === code);
  }
}

export const isApiError = (value: unknown): value is ApiError =>
  value instanceof ApiError;
